import { BOARD_SIZE, CENTRE_ROW, CENTRE_COL, } from './types.js';
import { cellAt, isEmpty } from './board.js';
import { detectDirection, applyPlacements, extractAllWords } from './word-extractor.js';
import { scoreMove } from './scorer.js';
function fail(code, message, details) {
    return { ok: false, error_code: code, message, details };
}
export function validateMove(board, placements, opts) {
    if (placements.length === 0)
        return fail('NO_PLACEMENTS', 'No placements provided');
    for (const p of placements) {
        if (p.row < 0 || p.row >= BOARD_SIZE || p.col < 0 || p.col >= BOARD_SIZE) {
            return fail('OUT_OF_BOUNDS', `Placement at ${p.row},${p.col} is out of bounds`);
        }
        if (cellAt(board, p.row, p.col).tile) {
            return fail('CELL_OCCUPIED', `Cell ${p.row},${p.col} is already occupied`);
        }
        if (p.is_blank && (!p.letter || p.letter.length !== 1)) {
            return fail('BLANK_MISSING_LETTER', 'Blank tile placement must specify a designated letter');
        }
    }
    let direction = detectDirection(placements);
    if (direction === null)
        return fail('OFF_AXIS', 'Placements must all be in one row or one column');
    // For a single tile, derive direction from existing neighbours (the new tile must form
    // a word with adjacent existing tiles).
    if (placements.length === 1) {
        const p = placements[0];
        const hasHoriz = (p.col > 0 && cellAt(board, p.row, p.col - 1).tile) ||
            (p.col < BOARD_SIZE - 1 && cellAt(board, p.row, p.col + 1).tile);
        const hasVert = (p.row > 0 && cellAt(board, p.row - 1, p.col).tile) ||
            (p.row < BOARD_SIZE - 1 && cellAt(board, p.row + 1, p.col).tile);
        if (!hasHoriz && !hasVert && !isEmpty(board)) {
            return fail('DISCONNECTED', 'Single-tile placement does not connect to any existing tile');
        }
        direction = hasHoriz ? 'across' : hasVert ? 'down' : 'across';
    }
    // Gap check: between min and max placement on the axis, every cell must be either
    // a placement or already occupied on the board (no empty cells in between).
    const sorted = [...placements].sort((a, b) => direction === 'across' ? a.col - b.col : a.row - b.row);
    const placementKeys = new Set(placements.map((p) => `${p.row},${p.col}`));
    if (direction === 'across') {
        const row = sorted[0].row;
        for (let col = sorted[0].col; col <= sorted[sorted.length - 1].col; col++) {
            if (!placementKeys.has(`${row},${col}`) && !cellAt(board, row, col).tile) {
                return fail('GAP_IN_PLACEMENTS', `Gap at ${row},${col}`);
            }
        }
    }
    else {
        const col = sorted[0].col;
        for (let row = sorted[0].row; row <= sorted[sorted.length - 1].row; row++) {
            if (!placementKeys.has(`${row},${col}`) && !cellAt(board, row, col).tile) {
                return fail('GAP_IN_PLACEMENTS', `Gap at ${row},${col}`);
            }
        }
    }
    // Empty-board first move must cover the centre.
    if (isEmpty(board)) {
        const coversCentre = placements.some((p) => p.row === CENTRE_ROW && p.col === CENTRE_COL);
        if (!coversCentre)
            return fail('NOT_COVERING_STAR_FIRST_MOVE', 'First move must cover the centre star');
    }
    else {
        // Must touch at least one existing tile.
        const touches = placements.some((p) => {
            for (const [dr, dc] of [
                [-1, 0],
                [1, 0],
                [0, -1],
                [0, 1],
            ]) {
                const nr = p.row + dr, nc = p.col + dc;
                if (nr < 0 || nr >= BOARD_SIZE || nc < 0 || nc >= BOARD_SIZE)
                    continue;
                if (cellAt(board, nr, nc).tile)
                    return true;
            }
            return false;
        });
        if (!touches)
            return fail('DISCONNECTED', 'Move does not connect to any existing tile');
    }
    // Apply placements and extract all formed words.
    const boardAfter = applyPlacements(board, placements);
    const { primary, crosses } = extractAllWords(boardAfter, placements, direction);
    // A single-tile placement only counts if it formed a primary word ≥2 OR a crossword.
    if (!primary && crosses.length === 0) {
        return fail('SINGLE_TILE_NOT_FORMING_WORD', 'Placement does not form any word of length ≥ 2');
    }
    // Dictionary check on every formed word.
    const allWords = primary ? [primary, ...crosses] : [...crosses];
    for (const w of allWords) {
        if (!opts.isInDictionary(w.word)) {
            return fail('WORD_NOT_IN_DICTIONARY', `${w.word} is not in the dictionary`, { word: w.word });
        }
    }
    const move_score = scoreMove(primary, crosses, placements.length);
    // ValidationSuccess.primary_word is non-optional; for the single-tile-no-primary case
    // we synthesise a one-cell "word" from the placement so callers always get a primary.
    const primaryOut = primary ?? {
        word: placements[0].letter,
        cells: [
            {
                row: placements[0].row,
                col: placements[0].col,
                letter: placements[0].letter,
                is_blank: placements[0].is_blank,
                is_newly_placed: true,
            },
        ],
    };
    return {
        ok: true,
        direction,
        board_after: boardAfter,
        primary_word: primaryOut,
        cross_words: crosses,
        move_score,
        bingo: placements.length === 7,
    };
}
//# sourceMappingURL=rules.js.map